import { useState, useEffect } from 'react';
import { getOnboardingAnalytics } from '../../services/onboardingService';
import BarChart from '../charts/BarChart';
import LineChart from '../charts/LineChart';

const OnboardingTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [onboardingData, setOnboardingData] = useState<any>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await getOnboardingAnalytics(30);
        setOnboardingData(data);
      } catch (error) {
        console.error('Error loading onboarding data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading onboarding analytics...</p>
      </div>
    );
  }

  if (!onboardingData) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>No onboarding data available</p>
      </div>
    );
  }

  // Funnel visualization
  const funnelData = {
    labels: onboardingData.funnel.map((step: any) => step.step),
    datasets: [
      {
        label: 'Users',
        data: onboardingData.funnel.map((step: any) => step.count),
        backgroundColor: '#2563eb',
      },
    ],
  };

  // Conversion rates
  const conversionRatesData = {
    labels: onboardingData.funnel.map((step: any) => step.step),
    datasets: [
      {
        label: 'Conversion Rate (%)',
        data: onboardingData.funnel.map((step: any) => step.conversionRate),
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
      },
    ],
  };

  // Drop-off analysis
  const dropOffData = {
    labels: onboardingData.dropOffPoints.map((point: any) => point.step),
    datasets: [
      {
        label: 'Drop-offs',
        data: onboardingData.dropOffPoints.map((point: any) => point.count),
        backgroundColor: '#ef4444',
      },
    ],
  };

  // First topic categories
  const categoryCounts = new Map<string, number>();
  onboardingData.firstExperiences.forEach((exp: any) => {
    categoryCounts.set(exp.firstTopicCategory, (categoryCounts.get(exp.firstTopicCategory) || 0) + 1);
  });

  const firstCategoriesData = {
    labels: Array.from(categoryCounts.keys()),
    datasets: [
      {
        label: 'Users',
        data: Array.from(categoryCounts.values()),
        backgroundColor: '#8b5cf6',
      },
    ],
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ marginBottom: '2rem', fontSize: '1.75rem', fontWeight: 'bold' }}>
        Onboarding & Activation Funnel
      </h1>

      {/* KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Onboarding Completion</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {onboardingData.activationMetrics.onboardingCompletionRate.toFixed(1)}%
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Time to First Topic</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {onboardingData.activationMetrics.timeToFirstTopic.toFixed(0)} min
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Time to First Session</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {onboardingData.activationMetrics.timeToFirstSession.toFixed(0)} min
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Overall Conversion</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {onboardingData.funnel[onboardingData.funnel.length - 1]?.conversionRate.toFixed(1)}%
          </h2>
        </div>
      </div>

      {/* Charts */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Onboarding Funnel</h3>
          <BarChart data={funnelData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Conversion Rates by Step</h3>
          <LineChart data={conversionRatesData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Drop-off Analysis</h3>
          <BarChart data={dropOffData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>First Topic Categories</h3>
          <BarChart data={firstCategoriesData} height={300} />
        </div>
      </div>

      {/* Funnel Details Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden',
      }}>
        <h3 style={{ padding: '1rem', margin: 0, borderBottom: '1px solid #e0e0e0' }}>
          Funnel Breakdown
        </h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Step</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Users</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Conversion Rate</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Drop-off</th>
            </tr>
          </thead>
          <tbody>
            {onboardingData.funnel.map((step: any, index: number) => (
              <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '1rem' }}>{step.step}</td>
                <td style={{ padding: '1rem' }}>{step.count}</td>
                <td style={{ padding: '1rem', color: step.conversionRate > 50 ? '#10b981' : '#ef4444' }}>
                  {step.conversionRate.toFixed(1)}%
                </td>
                <td style={{ padding: '1rem', color: '#ef4444' }}>{step.dropOff}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OnboardingTab;

